import { useState, useEffect, useCallback } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getAuth, saveAuth, fullLogout, getRefreshToken, hasCapability, tenantSlugFromToken } from '../utils/auth';
import { dbgV, dbgI, dbgW, rkLog } from '../utils/debug';
import { forceClearCachesAndReload } from '@quig/cxq-pwa-kit';
import { useModules } from '../hooks/useModules';
import { getSiteConfig } from '../utils/site';
import { Wordmark } from './Wordmark';

const COMP = 'Navbar';
import { apiFetch } from '../api/client';
import SubtenantPicker from './SubtenantPicker';
import UnreadBadge from './UnreadBadge';
import { isTenantSwitcherLocked, getActiveTenantSlug, setActiveTenantSlug } from '../utils/tenant';
import { getStoredTheme, setStoredTheme, applyTheme, type Theme } from '../utils/theme';
import { userInitials } from '../utils/userInitials';
import type { TenantOption } from '../types';

const isPWA = window.matchMedia('(display-mode: standalone)').matches
  || (navigator as unknown as { standalone?: boolean }).standalone === true;

// Grouped-nav item shape. activeMatch lets an item claim active-state on
// sub-routes (e.g. /reporting/neris/:callId) rather than just its exact path;
// when absent, exact-path matching is used.
type NavItem = { to: string; label: string; activeMatch?: (p: string) => boolean };

// DRY grouped dropdown for the top bar. Renders nothing if items is empty.
// Panel/overlay/item styling matches the former Documents dropdown verbatim,
// and always opens left-anchored downward (top:100%, left:0) per the
// no-overlap constraint.
//
// Declared at module scope (NOT inside Navbar's body) so it keeps a stable
// component identity across Navbar re-renders — otherwise React would
// unmount+remount the whole subtree on every render (open menu / online /
// tenant-fetch / route change), dropping focus and transitions inside open
// panels. Everything it needs is threaded in as props.
function NavDropdown({ label, id, items, openMenu, setOpenMenu, location, linkStyle, groupActive }: {
  label: string;
  id: 'operations' | 'reporting' | 'more';
  items: NavItem[];
  openMenu: null | 'operations' | 'reporting' | 'more';
  setOpenMenu: (v: null | 'operations' | 'reporting' | 'more') => void;
  location: ReturnType<typeof useLocation>;
  linkStyle: (to: string) => React.CSSProperties;
  groupActive: (items: NavItem[]) => boolean;
}) {
  if (items.length === 0) return null;
  const open = openMenu === id;
  const active = groupActive(items);
  return (
    <div style={{ position: 'relative' }}>
      <button
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => { rkLog('clicked:', 'Navbar', `${label} dropdown toggle -> ${!open ? 'open' : 'close'}`); setOpenMenu(open ? null : id); }}
        style={{
          ...linkStyle('/__nav_group__'),
          background: active ? 'var(--surface-2)' : 'transparent',
          color: active ? 'var(--text)' : 'var(--text-muted)',
          border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4,
        }}
      >
        {label} <span style={{ fontSize: 10 }}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <>
          <div style={{ position: 'fixed', inset: 0, zIndex: 149 }} onClick={() => setOpenMenu(null)} />
          <div style={{
            position: 'absolute', top: '100%', left: 0, marginTop: 4, zIndex: 150,
            background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius)', boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            minWidth: 180, overflow: 'hidden',
          }}>
            {items.map((item, idx) => (
              <Link
                key={item.to}
                to={item.to}
                onClick={() => { rkLog('clicked:', 'Navbar', `${label} > ${item.label} -> ${item.to}`); setOpenMenu(null); }}
                style={{
                  display: 'block', padding: '10px 14px', fontSize: 14, color: 'var(--text)',
                  textDecoration: 'none',
                  borderBottom: idx < items.length - 1 ? '1px solid var(--border)' : 'none',
                  background: (item.activeMatch ?? (p => p === item.to))(location.pathname) ? 'var(--surface-2)' : 'transparent',
                }}
              >
                {item.label}
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

export default function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const [online, setOnline] = useState(navigator.onLine);
  const [availableTenants, setAvailableTenants] = useState<TenantOption[]>([]);
  const [updateStatus, setUpdateStatus] = useState<'idle' | 'checking' | 'updating' | 'current'>('idle');
  const [theme, setTheme] = useState<Theme>(getStoredTheme());
  const [userMenuOpen, setUserMenuOpen] = useState(false);

  // Manual "check for updates" — now unconditional, via the library.
  //
  // WHY THE CONDITION WENT: this used to gate the whole cache-nuke on
  // `serverVersion !== __APP_VERSION__`. That is the SAME broken comparison
  // main.tsx carried — __APP_VERSION__ comes from frontend/package.json and
  // /api/version returned that identical string, so the branch was false on
  // every click and the button told the user "current" every time, no matter
  // how stale they actually were. A control that reports success while doing
  // nothing is worse than no control: it actively reassures.
  //
  // A manual button also cannot be the primary update path — the automatic
  // one in main.tsx is (see @quig/cxq-pwa-kit). This is now purely an escape
  // hatch, so it should not try to be clever about whether an update exists:
  // if a user reaches for it, they have a reason, and the honest behaviour is
  // to clear and reload unconditionally rather than second-guess them with a
  // comparison that has never once been true.
  const checkForUpdates = useCallback(async () => {
    setUpdateStatus('updating');
    try {
      await forceClearCachesAndReload();
    } catch {
      setUpdateStatus('idle');
    }
  }, []);

  // Switch tenant context: re-issue JWT scoped to the new tenant via the backend,
  // then navigate so all route components re-fetch with the updated token.
  const switchTenant = useCallback(async (slug: string) => {
    if (!slug) {
      dbgV(COMP, 'switchTenant: slug is empty, returning');
      return;
    }
    dbgI(COMP, 'switchTenant: option selected', { slug });
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      dbgW(COMP, 'switchTenant: no refresh token — cannot switch context');
      return;
    }
    try {
      const data = await apiFetch<{ access_token: string; refresh_token: string }>('/api/auth/switch-context', {
        method: 'POST',
        body: JSON.stringify({ tenant_slug: slug, refresh_token: refreshToken }),
      });
      const currentAuth = getAuth();
      if (!currentAuth) {
        dbgW(COMP, 'switchTenant: auth vanished mid-switch');
        return;
      }
      // The server can answer 200 with a token scoped to a DIFFERENT tenant
      // than the one requested: /api/auth/switch-context gates membership on
      // the (possibly stale) `tenants` JWT claim, and when live membership is
      // missing the on_refresh hook FALLS BACK to another tenant whose slug is
      // then minted verbatim. Trusting the 200 and storing `slug` would leave
      // the UI showing one tenant while the token authorizes another.
      //
      // So verify against the returned TOKEN, not the requested slug, and do it
      // BEFORE saveAuth so a wrong-tenant token is never persisted.
      const issuedSlug = tenantSlugFromToken(data.access_token);
      if (issuedSlug !== slug) {
        dbgW(COMP, 'switchTenant: server issued a token for a different tenant — discarding', {
          requested: slug,
          issued: issuedSlug,
        });
        return;
      }
      const updatedUser = { ...currentAuth.user, tenant_slug: slug };
      saveAuth(data.access_token, updatedUser, data.refresh_token);
      setActiveTenantSlug(slug);
      dbgI(COMP, 'switchTenant: new JWT saved', { slug });
    } catch (err) {
      dbgW(COMP, 'switchTenant: switch-context call failed', { error: String(err) });
      return;
    }
    setMenuOpen(false);
    navigate(location.pathname, { replace: true });
    dbgV(COMP, 'switchTenant: context change complete');
  }, [location.pathname, navigate]);

  const auth = getAuth();
  const isAdmin = auth?.user?.role === 'admin' || auth?.user?.is_super_admin;
  const canManageMembers = hasCapability('manage_members');
  const canManageScope = hasCapability('manage_scope');
  // Map markers has no module flag — capability alone gates it. view_markers is
  // granted to crew by default, so the Operations entry is visible to everyone
  // who can read markers; the admin-dropdown entry needs manage_markers.
  const canViewMarkers = hasCapability('view_markers');
  const canManageMarkers = hasCapability('manage_markers');
  const { hasModule } = useModules();

  const showDocuments = hasModule('protocols');
  const showNerisReporting = hasModule('neris');
  const showAdminPatients = hasModule('calls');
  const showAdminVehicles = hasModule('calls');
  const showAdminMabas = hasModule('mabas');
  const showAedInspections = hasModule('aed');
  const showInventory = hasModule('inventory');
  const showMedCheck = hasModule('med_check');
  // Capability gate for the Inspection Schedules admin entry — matches the
  // pattern used in InspectionSchedules.tsx and InventoryItems.tsx pages.
  const showInspectionSchedules =
    showMedCheck && hasCapability('manage_med_checks');

  // Which top-bar grouped dropdown is currently open (only one at a time).
  // null = none. Values: 'operations' | 'reporting' | 'more'.
  const [openMenu, setOpenMenu] = useState<null | 'operations' | 'reporting' | 'more'>(null);

  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  // Fetch available tenants from Sentinel /me (JWT claims) or fallback to /profile.
  // Depend on the token string, not the auth object — getAuth() returns a new object
  // reference on every call, which would re-fire this effect on every render.
  const authToken = auth?.token ?? null;
  useEffect(() => {
    if (!authToken) {
      dbgV(COMP, 'tenant fetch skipped — no auth state');
      return;
    }
    dbgI(COMP, 'fetching available tenants from /api/auth/me');
    apiFetch<{ tenants?: { slug: string; name: string; role: string }[]; available_tenants?: TenantOption[] }>('/api/auth/me', { skipLogoutOn401: true })
      .then(me => {
        // Sentinel /me returns "tenants" from JWT claims
        if (me.tenants && me.tenants.length > 0) {
          dbgI(COMP, '/api/auth/me: setting tenants from claims', { count: me.tenants.length, slugs: me.tenants.map(t => t.slug) });
          setAvailableTenants(me.tenants.map(t => ({ slug: t.slug, name: t.name })));
        } else if (me.available_tenants) {
          dbgI(COMP, '/api/auth/me: setting tenants from available_tenants', { count: me.available_tenants.length });
          setAvailableTenants(me.available_tenants);
        } else {
          dbgW(COMP, '/api/auth/me: no tenants in response — falling back to /profile');
          throw new Error('No tenants in /me response');
        }
      })
      .catch(err => {
        dbgW(COMP, '/api/auth/me failed, falling back to /profile', { error: err.message });
        apiFetch<{ available_tenants?: TenantOption[] }>('/api/auth/profile', { skipLogoutOn401: true })
          .then(p => {
            if (p.available_tenants) {
              dbgI(COMP, '/api/auth/profile: setting tenants', { count: p.available_tenants.length });
              setAvailableTenants(p.available_tenants);
            } else {
              dbgW(COMP, '/api/auth/profile: no available_tenants in response');
            }
          })
          .catch(err2 => {
            dbgW(COMP, '/api/auth/profile also failed', { error: err2.message });
          });
      });
  }, [authToken]);

  // Apply platform accent color on mount
  useEffect(() => {
    const cfg = getSiteConfig();
    document.documentElement.style.setProperty('--accent', cfg.accentColor);
  }, []);

  // Fetch user preferences (theme) on mount
  useEffect(() => {
    apiFetch<{ theme?: Theme; calendar_invite_enabled?: boolean; calendar_invite_allday?: boolean }>('/api/settings/preferences')
      .then(prefs => {
        if (prefs?.theme && prefs.theme !== theme) {
          setTheme(prefs.theme);
          setStoredTheme(prefs.theme);
          applyTheme(prefs.theme);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function toggleTheme() {
    const next: Theme = theme === 'dark' ? 'light' : 'dark';
    setTheme(next);
    setStoredTheme(next);
    applyTheme(next);
    // Fire-and-forget save to server
    apiFetch('/api/settings/preferences', {
      method: 'PUT',
      body: JSON.stringify({ theme: next }),
    }).catch(() => {});
  }

  function logout() {
    rkLog('clicked:', 'Navbar', 'Logout');
    fullLogout();
  }

  const linkStyle = (to: string): React.CSSProperties => ({
    padding: '6px 12px',
    borderRadius: 'var(--radius)',
    background: location.pathname === to ? 'var(--surface-2)' : 'transparent',
    color: location.pathname === to ? 'var(--text)' : 'var(--text-muted)',
    fontSize: 14,
  });

  // Derive display name for user menu
  const userDisplayName = auth?.user?.name
    || (auth?.user?.email ? auth.user.email.split('@')[0] : 'User');

  // Initials for the avatar badge (no photo/avatar URL exists in the user model).
  const initials = userInitials(auth?.user);

  // Circular initials avatar, reused by the desktop trigger + mobile header.
  const avatarBadge = (size: number) => (
    <span aria-hidden="true" style={{
      width: size, height: size, flexShrink: 0, borderRadius: '50%',
      background: 'var(--accent)', color: '#fff',
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      fontSize: Math.round(size * 0.42), fontWeight: 700, letterSpacing: '0.3px',
      lineHeight: 1, userSelect: 'none',
    }}>{initials}</span>
  );

  // Active tenant display name (shared by desktop label + mobile header).
  // The JWT's tenant_slug is authoritative — see comment in tenant switcher block below.
  const tenantSwitcherEnabled = availableTenants.length > 1
    && (!isTenantSwitcherLocked() || auth?.user?.is_super_admin);
  const activeTenantSlug = auth?.user?.tenant_slug || getActiveTenantSlug() || '';
  const activeTenantOption = availableTenants.find(t => t.slug === activeTenantSlug);
  const tenantDisplayName = activeTenantOption?.name
    || auth?.user?.tenant_name
    || activeTenantSlug
    || '';

  // ------------------------------------------------------------------
  // Variant B grouped nav IA.
  //
  // Each dropdown group is built by filtering a candidate list against the
  // SAME hasModule/hasCapability gates the flat nav used. A group whose
  // filtered list is empty is NOT rendered at all (its trigger vanishes).
  // This now applies to EVERY group including More — Station Display, its
  // former always-on entry, moved to Operations. (Operations is in turn
  // always non-empty, since Station Display is ungated.)
  // ------------------------------------------------------------------
  // (NavItem type is declared at module scope, above NavDropdown.)

  // Inline primary items (Calls, Schedule) + Members appended when gated.
  // Members = manage_members capability AND members module (matches the
  // canManageMembers + members-module gating the flat nav used).
  const primaryItems: NavItem[] = [
    { to: '/history', label: 'Calls', gate: hasModule('calls') },
    { to: '/schedule', label: 'Schedule', gate: hasModule('scheduling') },
    { to: '/members', label: 'Members', gate: canManageMembers && hasModule('members') },
  ].filter(i => i.gate).map(({ to, label }) => ({ to, label }));

  const showMessages = hasModule('messaging') && hasCapability('view_messaging');

  // Operations group: Station Display, AEDs, Inventory, Med Check, Map Markers,
  // plus the former Incidents entries (Disaster, Diversion) — the Incidents
  // group was retired rather than left as a single-item dropdown. Gates are
  // carried over verbatim from the groups these items came from.
  const operationsItems: NavItem[] = [
    { to: '/dashboard', label: 'Station Display', gate: true },
    { to: '/aeds', label: 'AEDs', gate: hasModule('aed') },
    { to: '/inventory', label: 'Inventory', gate: hasModule('inventory') },
    { to: '/med-check', label: 'Med Check', gate: hasModule('med_check') },
    { to: '/admin/map-markers', label: 'Map Markers', gate: canViewMarkers },
    { to: '/disaster', label: 'Disaster', gate: hasModule('disaster') },
    { to: '/diversion', label: 'Diversion', gate: hasModule('diversion') },
  ].filter(i => i.gate).map(({ to, label }) => ({ to, label }));

  // Reporting group: NERIS (existing). NERIS deep-links (e.g.
  // /reporting/neris/:callId) must keep the entry active, so match on the
  // /reporting prefix rather than exact path.
  const reportingItems: NavItem[] = [
    { to: '/reporting/neris', label: 'NERIS Reporting', gate: showNerisReporting, activeMatch: (p: string) => p.startsWith('/reporting') },
  ].filter(i => i.gate).map(({ to, label, activeMatch }) => ({ to, label, activeMatch }));

  // More (overflow) group: Location Directory + Training + the Documents items
  // (Protocols, Policies, Scope of Practice). Documents items gate on the
  // protocols module; Scope additionally needs manage_scope.
  //
  // Station Display moved to Operations, so More no longer carries an
  // always-on entry and CAN now filter down to empty. That is handled: both
  // NavDropdown (desktop) and the mobile drawer skip a group with no items,
  // so More simply does not render for a tenant with every item gated off.
  const moreItems: NavItem[] = [
    { to: '/location-directory', label: 'Location Directory', gate: hasModule('calls') },
    { to: '/training', label: 'Training', gate: hasModule('training') },
    // Protocol sub-routes (e.g. /protocol-library/:id) keep this entry active,
    // and /policies is part of the same Documents group.
    //
    // LABELS: these two entries point at two different views of the SAME
    // pooled protocol documents, so the labels have to say which is which --
    // "Protocols" and "Protocol Index" gave a reader no way to choose.
    //
    //   /protocol-library  PoolProtocols.tsx (925 lines) -- the MANAGEMENT
    //                      surface: upload, edit and delete documents, pool
    //                      administration, protocol-book generation. The
    //                      management controls are additionally admin-gated
    //                      INSIDE the page (isAdmin), but the entry itself is
    //                      visible to anyone with the protocols module.
    //   /protocol-index    ProtocolIndex.tsx (160 lines) -- explicitly
    //                      "standalone, read-only searchable list"; search by
    //                      title/category/filename, then View or Download.
    //
    // So: "Protocol Library" for the place documents are kept and managed,
    // "Browse Protocols" for the read-only finder. Routes, gates and
    // activeMatch are unchanged -- this is a labelling fix only.
    { to: '/protocol-library', label: 'Protocol Library', gate: showDocuments, activeMatch: (p: string) => p.startsWith('/protocol') || p === '/policies' },
    { to: '/protocol-index', label: 'Browse Protocols', gate: showDocuments },
    { to: '/policies', label: 'Policies', gate: showDocuments },
    { to: '/scope-of-practice', label: 'Scope of Practice', gate: showDocuments && canManageScope },
  ].filter(i => i.gate).map(({ to, label, activeMatch }) => ({ to, label, activeMatch }));

  // Reusable section-header style (ADMIN subheading in avatar menu, MABAS, etc.)
  const sectionHeaderStyle: React.CSSProperties = {
    fontSize: 11, fontWeight: 700, color: '#888', textTransform: 'uppercase',
    letterSpacing: '0.06em', padding: '6px 14px 2px',
  };
  // Mobile section-header style.
  const mobileSectionHeaderStyle: React.CSSProperties = {
    fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px',
    color: 'var(--text-muted)', marginTop: 8, marginBottom: 6, paddingLeft: 12, paddingRight: 12,
  };

  // Is any route in this group the active one? (drives the trigger highlight)
  // Each item's own activeMatch predicate is honoured so sub-routes (e.g.
  // /reporting/neris/:callId) still mark their group as active; items without
  // an activeMatch fall back to exact-path matching.
  const groupActive = (items: NavItem[]) =>
    items.some(i => (i.activeMatch ?? (p => p === i.to))(location.pathname));

  // Admin item list, each entry per its own gate, for the avatar dropdown.
  // MABAS is kept as its own labelled sub-section.
  const adminItems: (NavItem & { indent?: boolean })[] = isAdmin ? [
    { to: '/admin', label: 'Admin Dashboard', gate: true },
    // #1408: the inbox is the platform-wide ingest mailbox -- super-admin only.
    { to: '/admin/inbox', label: 'Inbox', gate: !!auth?.user?.is_super_admin },
    { to: '/admin/tenants', label: 'Tenants', gate: true },
    { to: '/admin/modules', label: 'Modules', gate: !!auth?.user?.is_super_admin },
    { to: '/admin/agencies', label: 'Agencies', gate: true },
    { to: '/admin/audit', label: 'Platform Audit Log', gate: !!auth?.user?.is_super_admin },
    { to: '/admin/patients', label: 'Patients', gate: showAdminPatients },
    { to: '/admin/vehicles', label: 'Fleet Vehicles', gate: showAdminVehicles },
    { to: '/admin/map-markers', label: 'Map Markers', gate: canManageMarkers },
    { to: '/aed-dashboard', label: 'AED Dashboard', gate: showAedInspections },
    { to: '/aed-inspections', label: 'AED Inspections', gate: showAedInspections },
    { to: '/inventory', label: 'Inventory Locations', gate: showInventory },
    { to: '/inventory/items', label: 'Inventory Items', gate: showInventory },
    { to: '/med-check', label: 'Med Check Dashboard', gate: showMedCheck },
    { to: '/med-check/inspections', label: 'Med Check Inspections', gate: showMedCheck },
    { to: '/admin/inspection-schedules', label: 'Inspection Schedules', gate: showInspectionSchedules },
    { to: '/med-check/dhs110-export', label: 'DHS 110 Export', gate: showMedCheck },
  ].filter(i => i.gate).map(({ to, label }) => ({ to, label })) : [];

  const mabasItems: NavItem[] = (isAdmin && showAdminMabas) ? [
    { to: '/admin/mabas/departments', label: 'Departments' },
    { to: '/admin/mabas/cards', label: 'Cards' },
    { to: '/admin/mabas/map', label: 'Map' },
    { to: '/admin/mabas/div110', label: 'Div 110 Forms' },
  ] : [];

  return (
    <nav style={{
      position: 'fixed', top: 0, left: 0, right: 0,
      // height folds in the top safe-area inset (see --nav-height in globals.css);
      // paddingTop pushes the bar's CONTENT below the notch so the logo/links sit
      // in the real 56px bar while the surface colour fills up under the status bar.
      // Left/right insets keep content clear of the notch in landscape.
      height: 'var(--nav-height)', background: 'var(--surface)',
      borderBottom: '1px solid var(--border)', zIndex: 100,
      display: 'flex', alignItems: 'center',
      paddingTop: 'env(safe-area-inset-top)',
      paddingLeft: 'max(16px, env(safe-area-inset-left))',
      paddingRight: 'max(16px, env(safe-area-inset-right))',
      gap: 12,
    }}>
      <Link to="/" style={{ flexShrink: 0, textDecoration: 'none' }}
        onClick={() => rkLog('clicked:', 'Navbar', 'brand wordmark link -> /')}>
        <Wordmark variant="plain" size={18} />
      </Link>

      <span style={{
        width: 8, height: 8, borderRadius: '50%',
        background: online ? 'var(--success)' : 'var(--warning)', flexShrink: 0,
      }} title={online ? 'Online' : 'Offline'} />

      {/* Desktop nav (Variant B grouped IA). Inline primary items + grouped
          dropdowns (Operations / Reporting) + a More overflow.
          min-width:0 lets this row shrink instead of pushing the right-side
          controls off the edge; the .nav-desktop-links class also clamps
          max-width to reserve ~280px for the right controls, reducing (not
          absolutely preventing) overlap with the tenant switcher zone.
          Do NOT add overflow:hidden — the group dropdowns are position:absolute
          children and would be clipped. (Row is display:none below 860px — the
          mobile hamburger drawer is used at narrower widths.) */}
      <div className="nav-desktop nav-desktop-links" style={{ display: 'flex', gap: 8, flex: 1, alignItems: 'center', minWidth: 0 }}>
        {primaryItems.map(l => (
          <Link key={l.to} to={l.to} style={linkStyle(l.to)} onClick={() => rkLog('clicked:', 'Navbar', `desktop nav link "${l.label}" -> ${l.to}`)}>{l.label}</Link>
        ))}
        {showMessages && (
          <Link to="/messages" style={{ ...linkStyle('/messages'), display: 'flex', alignItems: 'center' }} onClick={() => rkLog('clicked:', 'Navbar', 'desktop nav link "Messages" -> /messages')}>
            Messages
            <UnreadBadge />
          </Link>
        )}
        <NavDropdown label="Operations" id="operations" items={operationsItems} openMenu={openMenu} setOpenMenu={setOpenMenu} location={location} linkStyle={linkStyle} groupActive={groupActive} />
        <NavDropdown label="Reporting" id="reporting" items={reportingItems} openMenu={openMenu} setOpenMenu={setOpenMenu} location={location} linkStyle={linkStyle} groupActive={groupActive} />
        <NavDropdown label="More" id="more" items={moreItems} openMenu={openMenu} setOpenMenu={setOpenMenu} location={location} linkStyle={linkStyle} groupActive={groupActive} />
      </div>

      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
        {/* Tenant switcher or department name label (desktop). Uses tenantDisplayName
            hoisted to component scope so the mobile header can render the same value. */}
        {tenantSwitcherEnabled ? (
          <select
            className="nav-desktop"
            value={activeTenantSlug}
            onChange={e => {
              rkLog('context switcher: option selected ->', e.target.value);
              switchTenant(e.target.value);
            }}
            style={{ fontSize: 12, padding: '4px 8px', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', color: 'var(--text)', cursor: 'pointer' }}
            title="Switch department context"
          >
            {availableTenants.map(t => (
              <option key={t.slug} value={t.slug}>{t.name}</option>
            ))}
          </select>
        ) : tenantDisplayName ? (
          <span className="nav-desktop" style={{ fontSize: 12, color: 'var(--text-muted)', padding: '4px 8px' }}>
            {tenantDisplayName}
          </span>
        ) : null}
        {/* Division (subtenant) switcher — renders null unless the user has
            multiple switchable divisions. */}
        <span className="nav-desktop"><SubtenantPicker /></span>
        {/* Desktop user menu dropdown */}
        <div className="nav-desktop" style={{ position: 'relative' }}>
          <button
            onClick={() => { rkLog('clicked:', 'Navbar', `User menu toggle -> ${!userMenuOpen ? 'open' : 'close'}`); setUserMenuOpen(v => !v); }}
            aria-label="User menu"
            aria-haspopup="menu"
            aria-expanded={userMenuOpen}
            title={userDisplayName}
            style={{
              padding: '5px 10px 5px 5px', border: 'none', cursor: 'pointer',
              background: userMenuOpen ? 'var(--surface-2)' : 'transparent',
              color: 'var(--text)', fontSize: 13, borderRadius: 999,
              display: 'flex', alignItems: 'center', gap: 8, minHeight: 44,
            }}
          >
            {/* Initials avatar representing the current user */}
            {avatarBadge(30)}
            <span style={{ maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{userDisplayName}</span>
            <span style={{ fontSize: 10 }}>{userMenuOpen ? '\u25B2' : '\u25BC'}</span>
          </button>
          {userMenuOpen && (
            <>
              <div style={{ position: 'fixed', inset: 0, zIndex: 149 }} onClick={() => setUserMenuOpen(false)} />
              <div style={{
                position: 'absolute', top: '100%', right: 0, marginTop: 4, zIndex: 150,
                background: 'var(--surface)', border: '1px solid var(--border)',
                borderRadius: 'var(--radius)', boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                minWidth: 220, overflow: 'hidden',
              }}>
                {/* User identity header: avatar + name + email/role */}
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 10,
                  padding: '12px 14px', borderBottom: '1px solid var(--border)',
                }}>
                  {avatarBadge(36)}
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {userDisplayName}
                    </div>
                    {auth?.user?.email && (
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {auth.user.email}
                      </div>
                    )}
                  </div>
                </div>
                {/* Profile (distinct from Settings — account/credentials view) */}
                <Link to="/profile" onClick={() => { rkLog('clicked:', 'Navbar', 'User > Profile -> /profile'); setUserMenuOpen(false); }} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '10px 14px', fontSize: 14, color: 'var(--text)',
                  textDecoration: 'none', borderBottom: '1px solid var(--border)',
                  background: location.pathname === '/profile' ? 'var(--surface-2)' : 'transparent',
                }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                  Profile
                </Link>
                {/* Settings */}
                <Link to="/settings" onClick={() => { rkLog('clicked:', 'Navbar', 'User > Settings -> /settings'); setUserMenuOpen(false); }} style={{
                  display: 'flex', alignItems: 'center', gap: 8,
                  padding: '10px 14px', fontSize: 14, color: 'var(--text)',
                  textDecoration: 'none', borderBottom: '1px solid var(--border)',
                  background: location.pathname === '/settings' ? 'var(--surface-2)' : 'transparent',
                }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="3" />
                    <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                  </svg>
                  Settings
                </Link>
                {/* Admin section (moved out of the top bar into the avatar
                    dropdown). Only for admins; each item gated per its module. */}
                {isAdmin && (adminItems.length > 0 || mabasItems.length > 0) && (
                  <>
                    <div style={sectionHeaderStyle}>Admin</div>
                    {adminItems.map(item => (
                      <Link key={item.to} to={item.to} onClick={() => { rkLog('clicked:', 'Navbar', `Admin > ${item.label} -> ${item.to}`); setUserMenuOpen(false); }} style={{
                        display: 'block', padding: '10px 14px', fontSize: 14, color: 'var(--text)',
                        textDecoration: 'none',
                        background: location.pathname === item.to ? 'var(--surface-2)' : 'transparent',
                      }}>
                        {item.label}
                      </Link>
                    ))}
                    {mabasItems.length > 0 && <>
                      <div style={sectionHeaderStyle}>MABAS</div>
                      {mabasItems.map(item => (
                        <Link key={item.to} to={item.to} onClick={() => { rkLog('clicked:', 'Navbar', `Admin > MABAS > ${item.label} -> ${item.to}`); setUserMenuOpen(false); }} style={{
                          display: 'block', padding: '8px 14px 8px 24px', fontSize: 14, color: 'var(--text)',
                          textDecoration: 'none',
                          background: location.pathname === item.to ? 'var(--surface-2)' : 'transparent',
                        }}>
                          {item.label}
                        </Link>
                      ))}
                    </>}
                    <div style={{ borderBottom: '1px solid var(--border)' }} />
                  </>
                )}
                {/* Theme toggle */}
                <button
                  onClick={() => { rkLog('clicked:', 'Navbar', `Theme toggle -> ${theme === 'dark' ? 'light' : 'dark'}`); toggleTheme(); }}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '10px 14px', fontSize: 14, color: 'var(--text)',
                    background: 'transparent', border: 'none', borderBottom: '1px solid var(--border)',
                    cursor: 'pointer', textAlign: 'left',
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {theme === 'dark' ? (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
                      </svg>
                    ) : (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="5" />
                        <line x1="12" y1="1" x2="12" y2="3" />
                        <line x1="12" y1="21" x2="12" y2="23" />
                        <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" />
                        <line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
                        <line x1="1" y1="12" x2="3" y2="12" />
                        <line x1="21" y1="12" x2="23" y2="12" />
                        <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" />
                        <line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
                      </svg>
                    )}
                    {theme === 'dark' ? 'Dark Mode' : 'Light Mode'}
                  </span>
                  {/* Toggle switch visual */}
                  <span style={{
                    width: 36, height: 20, borderRadius: 10, position: 'relative',
                    background: theme === 'light' ? 'var(--accent)' : 'var(--border)',
                    transition: 'background 0.2s', flexShrink: 0,
                  }}>
                    <span style={{
                      position: 'absolute', top: 2, left: theme === 'light' ? 18 : 2,
                      width: 16, height: 16, borderRadius: '50%', background: '#fff',
                      transition: 'left 0.2s',
                    }} />
                  </span>
                </button>
                {/* Version / Update check */}
                {isPWA ? (
                  <button
                    onClick={() => { rkLog('clicked:', 'Navbar', 'Check for updates'); checkForUpdates(); }}
                    disabled={updateStatus === 'checking' || updateStatus === 'updating'}
                    style={{
                      width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                      padding: '10px 14px', fontSize: 13, color: updateStatus === 'current' ? 'var(--success)' : updateStatus === 'updating' ? 'var(--accent)' : 'var(--text-muted)',
                      background: 'transparent', border: 'none', borderBottom: '1px solid var(--border)',
                      cursor: updateStatus === 'checking' || updateStatus === 'updating' ? 'wait' : 'pointer',
                      textAlign: 'left',
                    }}
                  >
                    {updateStatus === 'checking' ? 'Checking for updates...'
                      : updateStatus === 'updating' ? 'Updating...'
                      : updateStatus === 'current' ? `v${__APP_VERSION__} \u2014 Up to date`
                      : `v${__APP_VERSION__} \u2014 Check for updates`}
                  </button>
                ) : (
                  <div style={{
                    padding: '10px 14px', fontSize: 12, color: 'var(--text-muted)',
                    borderBottom: '1px solid var(--border)',
                  }}>
                    v{__APP_VERSION__}
                  </div>
                )}
                {/* Logout */}
                <button
                  onClick={() => { setUserMenuOpen(false); logout(); }}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                    padding: '10px 14px', fontSize: 14, color: 'var(--danger)',
                    background: 'transparent', border: 'none',
                    cursor: 'pointer', textAlign: 'left',
                  }}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                    <polyline points="16 17 21 12 16 7" />
                    <line x1="21" y1="12" x2="9" y2="12" />
                  </svg>
                  Logout
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Mobile tenant name \u2014 visible at all times on small viewports so users
          can see which department context they're in without opening the menu.
          No `display` in inline style \u2014 visibility is controlled by .nav-mobile
          rules in globals.css (display:none >= 860px, inline-flex <= 859px). */}
      {tenantDisplayName && (
        <span
          className="nav-mobile"
          style={{
            fontSize: 12,
            color: 'var(--text-muted)',
            marginRight: 8,
            maxWidth: 140,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            alignItems: 'center',
          }}
          title={tenantDisplayName}
        >
          {tenantDisplayName}
        </span>
      )}

      {/* Mobile user avatar \u2014 right side of the header on phones. Opens the
          hamburger panel (which carries the user actions: profile/settings/logout),
          so we don't stack a second dropdown on a tiny screen. Visibility is the
          .nav-mobile rule in globals.css (shown <=859px, hidden on desktop). */}
      <button
        className="nav-mobile"
        aria-label="User menu"
        aria-haspopup="menu"
        aria-expanded={menuOpen}
        title={userDisplayName}
        onClick={() => { rkLog('clicked:', 'Navbar', `mobile avatar -> ${!menuOpen ? 'open' : 'close'} menu`); setMenuOpen(!menuOpen); }}
        style={{
          padding: 4, marginRight: 4, border: 'none', background: 'transparent',
          cursor: 'pointer', minHeight: 44, minWidth: 44,
          alignItems: 'center', justifyContent: 'center',
        }}
      >
        {avatarBadge(30)}
      </button>

      {/* Mobile hamburger */}
      <button
        className="btn-ghost nav-hamburger"
        style={{ padding: '6px 8px' }}
        onClick={() => { rkLog('clicked:', 'Navbar', `hamburger menu toggle -> ${!menuOpen ? 'open' : 'close'}`); setMenuOpen(!menuOpen); }}
      >
        {menuOpen ? '\u2715' : '\u2630'}
      </button>

      {menuOpen && (
        <div style={{
          position: 'fixed', top: 'var(--nav-height)', left: 0, right: 0,
          background: 'var(--surface)', borderBottom: '1px solid var(--border)',
          // Cap to the space below the nav and scroll if the link list is long, with
          // a bottom safe-area inset so the last item clears the home indicator.
          maxHeight: 'calc(100dvh - var(--nav-height))', overflowY: 'auto',
          paddingTop: 12,
          paddingBottom: 'max(12px, env(safe-area-inset-bottom))',
          paddingLeft: 'max(16px, env(safe-area-inset-left))',
          paddingRight: 'max(16px, env(safe-area-inset-right))',
          display: 'flex', flexDirection: 'column', gap: 2, zIndex: 99,
        }}>
          {/* User identity header (mobile) */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '8px 12px 12px', marginBottom: 4,
            borderBottom: '1px solid var(--border)',
          }}>
            {avatarBadge(40)}
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {userDisplayName}
              </div>
              {auth?.user?.email && (
                <div style={{ fontSize: 12, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {auth.user.email}
                </div>
              )}
            </div>
          </div>
          {/* Mobile drawer — grouped IA (Variant B). Empty sections omitted. */}
          {(() => {
            const mobileLink = (item: NavItem, group: string) => {
              const isActive = (item.activeMatch ?? (p => p === item.to))(location.pathname);
              return (
                <Link
                  key={item.to}
                  to={item.to}
                  style={{ padding: '10px 12px', borderRadius: 'var(--radius)', color: isActive ? 'var(--text)' : 'var(--text-muted)', fontWeight: isActive ? 600 : 400 }}
                  onClick={() => { rkLog('clicked:', 'Navbar', `mobile ${group} "${item.label}" -> ${item.to}`); setMenuOpen(false); }}
                >
                  {item.label}
                </Link>
              );
            };
            return (
              <>
                {(primaryItems.length > 0 || showMessages) && (
                  <>
                    <div style={mobileSectionHeaderStyle}>Primary</div>
                    {primaryItems.map(i => mobileLink(i, 'Primary'))}
                    {showMessages && (
                      <Link
                        to="/messages"
                        style={{
                          padding: '10px 12px', borderRadius: 'var(--radius)', display: 'flex', alignItems: 'center',
                          color: location.pathname === '/messages' ? 'var(--text)' : 'var(--text-muted)',
                          fontWeight: location.pathname === '/messages' ? 600 : 400,
                        }}
                        onClick={() => { rkLog('clicked:', 'Navbar', 'mobile Primary "Messages" -> /messages'); setMenuOpen(false); }}
                      >
                        Messages
                        <UnreadBadge />
                      </Link>
                    )}
                  </>
                )}
                {operationsItems.length > 0 && (
                  <>
                    <div style={mobileSectionHeaderStyle}>Operations</div>
                    {operationsItems.map(i => mobileLink(i, 'Operations'))}
                  </>
                )}
                {reportingItems.length > 0 && (
                  <>
                    <div style={mobileSectionHeaderStyle}>Reporting</div>
                    {reportingItems.map(i => mobileLink(i, 'Reporting'))}
                  </>
                )}
                {moreItems.length > 0 && (
                  <>
                    <div style={mobileSectionHeaderStyle}>More</div>
                    {moreItems.map(i => mobileLink(i, 'More'))}
                  </>
                )}
                {isAdmin && (adminItems.length > 0 || mabasItems.length > 0) && (
                  <>
                    <div style={mobileSectionHeaderStyle}>Admin</div>
                    {adminItems.map(i => mobileLink(i, 'Admin'))}
                    {mabasItems.map(i => mobileLink(i, 'Admin > MABAS'))}
                  </>
                )}
              </>
            );
          })()}
          {/* ACCOUNT */}
          <div style={{ borderTop: '1px solid var(--border)', marginTop: 4, paddingTop: 4, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <div style={mobileSectionHeaderStyle}>Account</div>
            <Link to="/profile" style={{ padding: '12px', borderRadius: 'var(--radius)', color: 'var(--text-muted)' }} onClick={() => { rkLog('clicked:', 'Navbar', 'mobile Profile -> /profile'); setMenuOpen(false); }}>
              Profile
            </Link>
            <Link to="/settings" style={{ padding: '12px', borderRadius: 'var(--radius)', color: 'var(--text-muted)' }} onClick={() => { rkLog('clicked:', 'Navbar', 'mobile Settings -> /settings'); setMenuOpen(false); }}>
              Settings
            </Link>
            {/* Theme toggle (mobile) */}
            <button
              onClick={() => { rkLog('clicked:', 'Navbar', `mobile theme toggle -> ${theme === 'dark' ? 'light' : 'dark'}`); toggleTheme(); }}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '10px 12px', borderRadius: 'var(--radius)',
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: 'var(--text-muted)', fontSize: 14, textAlign: 'left',
              }}
            >
              <span>{theme === 'dark' ? 'Dark Mode' : 'Light Mode'}</span>
              <span style={{
                width: 36, height: 20, borderRadius: 10, position: 'relative',
                background: theme === 'light' ? 'var(--accent)' : 'var(--border)',
                transition: 'background 0.2s', flexShrink: 0,
              }}>
                <span style={{
                  position: 'absolute', top: 2, left: theme === 'light' ? 18 : 2,
                  width: 16, height: 16, borderRadius: '50%', background: '#fff',
                  transition: 'left 0.2s',
                }} />
              </span>
            </button>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              {isPWA ? (
                <button
                  onClick={() => { rkLog('clicked:', 'Navbar', 'mobile check for updates'); checkForUpdates(); }}
                  disabled={updateStatus === 'checking' || updateStatus === 'updating'}
                  style={{
                    padding: '8px 12px', background: 'none', border: 'none', cursor: 'pointer',
                    color: updateStatus === 'current' ? 'var(--success)' : updateStatus === 'updating' ? 'var(--accent)' : 'var(--text-muted)',
                    fontSize: 12,
                  }}
                >
                  {updateStatus === 'checking' ? 'Checking...'
                    : updateStatus === 'updating' ? 'Updating...'
                    : updateStatus === 'current' ? `v${__APP_VERSION__} ✓`
                    : `v${__APP_VERSION__}`}
                </button>
              ) : (
                <span style={{ padding: '8px 12px', fontSize: 12, color: 'var(--text-muted)' }}>v{__APP_VERSION__}</span>
              )}
              <button className="btn-ghost" style={{ padding: '6px 12px', fontSize: 13 }} onClick={logout}>Logout</button>
            </div>
          </div>
          {/* DEPARTMENT — tenant switcher (mobile) */}
          {availableTenants.length > 1 && (!isTenantSwitcherLocked() || auth?.user?.is_super_admin) && (
            <div style={{ borderTop: '1px solid var(--border)', marginTop: 4, paddingTop: 8, paddingBottom: 4, paddingLeft: 12, paddingRight: 12 }}>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>Department</div>
              <select
                value={auth?.user?.tenant_slug || getActiveTenantSlug() || ''}
                onChange={e => {
                  rkLog('context switcher (mobile): option selected ->', e.target.value);
                  switchTenant(e.target.value);
                }}
                style={{ width: '100%', fontSize: 14, padding: '10px 12px', background: 'var(--surface-2)', border: '1px solid var(--border)', borderRadius: 'var(--radius)', color: 'var(--text)', cursor: 'pointer' }}
              >
                {availableTenants.map(t => (
                  <option key={t.slug} value={t.slug}>{t.name}</option>
                ))}
              </select>
            </div>
          )}
          {/* Division (subtenant) switcher (mobile) — renders null unless the
              user has multiple switchable divisions. */}
          <div style={{ paddingLeft: 12, paddingRight: 12 }}>
            <SubtenantPicker />
          </div>
        </div>
      )}
    </nav>
  );
}
